import type { ScaleReferenceSnapshot } from './scale-references'
import type { GeminiAspectRatio, GeminiImageGenerationSettings, PlaygroundToolType, WorkflowV2Stage } from './types'

export const CHARACTER_SHEET_IMAGE_PREFIX = 'character-sheets'
export const MAX_CHARACTER_SHEETS = 1
export const MAX_GENERATION_INPUT_IMAGES = 14
export const REFERENCE_ASSET_CATEGORIES = ['sheet', 'appearance', 'scale'] as const
export type ReferenceAssetCategory = (typeof REFERENCE_ASSET_CATEGORIES)[number]
export type ReferenceAssetContext = PlaygroundToolType | WorkflowV2Stage | 'character-sheet' | 'scale-reference'

export interface ReferenceAssetSelection {
  useAppearanceReferences?: boolean
  useCharacterSheet?: boolean
  useScaleReferences?: boolean
}

export const DEFAULT_REFERENCE_ASSET_SELECTION: ReferenceAssetSelection = {
  useAppearanceReferences: true,
  useCharacterSheet: true,
  useScaleReferences: true
}

export interface ReferenceAssetSnapshot extends ReferenceAssetSelection, ScaleReferenceSnapshot {
  /** Present, including [], on new supported runs. Missing preserves legacy resolution. */
  appearanceReferenceImageKeys?: string[]
  characterSheetImageKey?: string
  scaleReferenceBriefs?: Record<string, string>
}

export interface CharacterReferenceAssets {
  appearanceReferenceImageKeys: string[]
  characterSheetImageKey?: string
  scaleReferenceImageKeys: string[]
  scaleReferenceBriefs?: Record<string, string>
}

export interface ReferenceBuilderInput {
  kind: 'character-sheet' | 'scale-reference'
  /** Scale grids only. Character sheets always use the fixed eight-view layout; older runs may still carry a count. */
  panelCount?: 4 | 6 | 9
}

export const DEFAULT_SCALE_REFERENCE_PANEL_COUNT = 4
export const CHARACTER_SHEET_PANEL_COUNT = 8

export type PlaygroundHistoryScope = 'playground' | 'reference'

export interface CreateReferenceBuilderRunRequest extends ReferenceAssetSelection {
  runId: string
  experimentId?: string
  parentRunId?: string
  character?: string
  variantCount: 1 | 2 | 3 | 4
  userInput: string
  referenceBuilder: ReferenceBuilderInput
  referenceImageKeys?: string[]
  geminiImageSettings?: GeminiImageGenerationSettings
}

export function supportsReferenceAsset(context: ReferenceAssetContext, category: ReferenceAssetCategory): boolean {
  if (category === 'scale') return ['scene', 'background', 'composition', 'inpaint'].includes(context)
  if (category === 'sheet')
    return ['character', 'character-nanobanana', 'scene', 'composition', 'inpaint', 'scale-reference'].includes(context)
  return [
    'character',
    'character-nanobanana',
    'scene',
    'composition',
    'inpaint',
    'character-sheet',
    'scale-reference'
  ].includes(context)
}

export function referenceBuilderAspectRatio(input: ReferenceBuilderInput): GeminiAspectRatio {
  return input.kind === 'scale-reference' && input.panelCount === 6 ? '3:2' : '1:1'
}

const CHARACTER_SHEET_LAYOUT = [
  'Top row, three full-body panels: three-quarter view, front view, back view.',
  'Middle row, two upper-body panels (head to waist): three-quarter view, front view.',
  'Bottom row, three head close-ups: back of the head, front with an open smile, front with a closed smile.'
].join(' ')

export function buildReferenceBuilderPrompt(input: ReferenceBuilderInput, brief: string): string {
  const identity =
    'Every panel depicts the same character from the attached identity images. Preserve identity, anatomy, materials, colors, outfit, and any existing logos or markings on the outfit. Never invent a different character.'
  const body =
    input.kind === 'character-sheet'
      ? [
          `Default sheet layout: create one character model sheet containing exactly ${CHARACTER_SHEET_PANEL_COUNT} clearly separated panels in three rows (3, 2, 3), centered on a pure white background.`,
          'Use the attached identity images to depict the same character consistently. Apply any explicitly requested appearance changes consistently across the sheet; preserve identity details that the user does not change.',
          CHARACTER_SHEET_LAYOUT,
          'Use the same neutral standing pose and neutral expression in every panel except the two smiling head close-ups. Keep consistent studio lighting, camera height, and scale within each row. No captions, labels, titles, numbers, or text of any kind; no scenery, added logos, or watermarks.'
        ]
      : [
          `Create one reference image containing exactly ${scalePanelCount(input)} clearly separated panels in ${scaleLayout(scalePanelCount(input))}.`,
          identity,
          'Create a scale comparison grid. Use familiar comparison objects with coherent perspective and believable physical sizes. Keep the character at the same intended real-world size throughout. Follow the supplied size/comparison brief; never invent an exact measurement that it does not supply. No captions, labels, or text.'
        ]
  if (input.kind === 'character-sheet')
    body.unshift(
      'The sheet specifications below are defaults. Explicit user instructions take priority over conflicting defaults, including layout, views, poses, expressions, background, text, outfit, colors, and materials. Keep defaults that the user does not change. The output canvas aspect ratio is controlled by the generation settings.'
    )
  return [...body, brief ? `User instructions: ${brief}` : ''].filter(Boolean).join('\n\n')
}

function scalePanelCount(input: ReferenceBuilderInput): 4 | 6 | 9 {
  return input.panelCount ?? DEFAULT_SCALE_REFERENCE_PANEL_COUNT
}

function scaleLayout(panelCount: 4 | 6 | 9): string {
  return panelCount === 6 ? '3 columns and 2 rows' : panelCount === 9 ? '3 columns and 3 rows' : '2 columns and 2 rows'
}

export function referenceAssetSnapshot(input: ReferenceAssetSnapshot): ReferenceAssetSnapshot {
  const {
    useAppearanceReferences,
    useCharacterSheet,
    useScaleReferences,
    appearanceReferenceImageKeys,
    characterSheetImageKey,
    scaleReferenceImageKeys,
    scaleReferenceBriefs
  } = input
  return {
    useAppearanceReferences,
    useCharacterSheet,
    useScaleReferences,
    appearanceReferenceImageKeys,
    characterSheetImageKey,
    scaleReferenceImageKeys,
    scaleReferenceBriefs
  }
}
